import {createContext, ReactElement, useContext, useEffect, useState} from 'react';
import {GetUserProfileRequest, GetUserProfileResponse, IApiUserProto} from './compiled';
import {useAuth0} from "@auth0/auth0-react";
import {useRpcSender} from "./RpcSender";

export const UserProfileContext = createContext<{
  profile: IApiUserProto | null | undefined,
  setProfile: (profile: IApiUserProto | null | undefined) => void,
  profileLoading: boolean,
  profileError: string | undefined,
}>({
  profile: undefined,
  setProfile: () => {},
  profileLoading: true,
  profileError: undefined,
});

export const useProfile = () => {
  return useContext(UserProfileContext)
}

interface Props {
  children?: ReactElement
}

/**
 * This should return the proto configuration for this unicorn.
 * It contains all the unicorn's special characteristics, like
 * the logo, description, which modules are on the website, etc.
 *
 *
 * @return {JSX.Element}
 * @constructor
 */
export const UserProfileProvider = (props: Props) => {

  const {user, isLoading} = useAuth0();
  const rpcSender = useRpcSender();

  const [userProfile, setUserProfile] = useState<IApiUserProto | null>();
  const [profileLoading, setProfileLoading] = useState<boolean>(true);
  const [profileError, setProfileError] = useState<string>();

  useEffect(() => {

    // Don't do anything if the user is laoding.
    if (isLoading) {
      return;
    }
    
    // The authenticated user is null (signout?). Clear any existing profile.
    if (!user) {
      setUserProfile(undefined);
      setProfileError(undefined);
      setProfileLoading(false);
      return;
    }

    // Download it immediately at the beginning...
    console.log('[Fetching user profile...]');
    setProfileLoading(true);

    rpcSender.send("get_user_profile",
        GetUserProfileRequest.encode(new GetUserProfileRequest()).finish())
        .then(response => {
          let profileResponse = GetUserProfileResponse.decode(response);
          console.log("User profile: " + JSON.stringify(profileResponse.toJSON()))
          setUserProfile(profileResponse.profile);
        })
        .catch(error => {
          console.log('user error', error)
          setProfileError(error)
        })
        .finally(() => {
          setProfileLoading(false);
        });

  }, [user, isLoading]);

  return (<UserProfileContext.Provider value={{
    profile: userProfile,
    setProfile: setUserProfile,
    profileLoading: profileLoading,
    profileError: profileError
  }}>
    {props.children}
  </UserProfileContext.Provider>);
};
